import restaurantPrinterApi from '../../api/restaurant/restaurantPrinterApi';
import {
  CustomResponse,
  RestaurantPreparationStationDTO,
  RestaurantPreparationStationFilters,
  RestaurantPreparationStationForm,
  RestaurantPreparationStationPage,
  RestaurantStationAssignmentDTO,
  RestaurantStationAssignmentForm,
  RestaurantStationUserOption,
} from '../../types/restaurant/RestaurantPreparationStationTypes';
import { appendRestaurantQuery } from './restaurantPageQuery';

const stationPayload = (form: RestaurantPreparationStationForm) => ({
  name: form.name.trim(),
  description: form.description.trim() || null,
  printerId: form.printerId === '' ? null : Number(form.printerId),
  displayOrder: Number(form.displayOrder),
  estimatedPreparationMinutes:
    form.estimatedPreparationMinutes === ''
      ? null
      : Number(form.estimatedPreparationMinutes),
  enabled: form.enabled,
  ...(form.id ? { active: form.active } : {}),
});

const RestaurantPreparationStationService = {
  list: async (
    branchId: number,
    filters: RestaurantPreparationStationFilters
  ) => {
    const query = appendRestaurantQuery(
      new URLSearchParams({ page: String(filters.page), size: String(filters.size) }),
      {
        search: filters.search?.trim(),
        active: filters.active,
        enabled: filters.enabled,
        printerId: filters.printerId,
      }
    );
    const response = await restaurantPrinterApi.fetchCustom(
      `/branches/${branchId}/preparation-stations?${query}`
    );
    return response.data as RestaurantPreparationStationPage;
  },
  get: async (id: number) => {
    const response = await restaurantPrinterApi.fetchCustom(`/preparation-stations/${id}`);
    return response.data as CustomResponse<RestaurantPreparationStationDTO>;
  },
  create: async (branchId: number, form: RestaurantPreparationStationForm) => {
    const response = await restaurantPrinterApi.addCustom(
      `/branches/${branchId}/preparation-stations`,
      stationPayload(form)
    );
    return response.data as CustomResponse<RestaurantPreparationStationDTO>;
  },
  update: async (id: number, form: RestaurantPreparationStationForm) => {
    const response = await restaurantPrinterApi.modifyCustom(
      `/preparation-stations/${id}`,
      stationPayload(form)
    );
    return response.data as CustomResponse<RestaurantPreparationStationDTO>;
  },
  deactivate: async (id: number) => {
    const response = await restaurantPrinterApi.removeCustom(`/preparation-stations/${id}`);
    return response.data as CustomResponse<null>;
  },
  listAssignments: async (stationId: number, activeOnly = false) => {
    const response = await restaurantPrinterApi.fetchCustom(
      `/preparation-stations/${stationId}/assignments?activeOnly=${activeOnly}`
    );
    return response.data as CustomResponse<RestaurantStationAssignmentDTO[]>;
  },
  listUserStations: async (userId: number) => {
    const response = await restaurantPrinterApi.fetchCustom(
      `/users/${userId}/preparation-stations`
    );
    return response.data as CustomResponse<RestaurantStationAssignmentDTO[]>;
  },
  assign: async (stationId: number, form: RestaurantStationAssignmentForm) => {
    const response = await restaurantPrinterApi.addCustom(
      `/preparation-stations/${stationId}/assignments`,
      { userId: Number(form.userId), primaryAssignment: form.primaryAssignment }
    );
    return response.data as CustomResponse<RestaurantStationAssignmentDTO>;
  },
  updateAssignment: async (
    assignmentId: number,
    data: { primaryAssignment?: boolean; active?: boolean }
  ) => {
    const response = await restaurantPrinterApi.modifyCustom(
      `/preparation-station-assignments/${assignmentId}`,
      data
    );
    return response.data as CustomResponse<RestaurantStationAssignmentDTO>;
  },
  deactivateAssignment: async (assignmentId: number) => {
    const response = await restaurantPrinterApi.removeCustom(
      `/preparation-station-assignments/${assignmentId}`
    );
    return response.data as CustomResponse<null>;
  },
  listUsers: async (branchId: number) => {
    const response = await restaurantPrinterApi.fetchCustom(`/branches/${branchId}/staff`);
    const body = response.data;
    return (body?.data ?? []) as RestaurantStationUserOption[];
  },
};

export default RestaurantPreparationStationService;
